import React, {
  createRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Button } from 'semantic-ui-react'
import { IChoiceOption, ISelectedChoice } from '../../models/Choice'
import Conditional from '../common/Conditional'
import { isEmpty, isURL } from '../../utilities/commonFunctions'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle'
import FormDependencyLoaderContext from '../context/FormDependencyLoaderContext'
import GetHelpOptionContent from './GetHelpOptionContent'
import GetHelpOption from './GetHelpOption'
import { createMarkup } from '../../models/Common'
import CalloutDisplay from '../common/CalloutDisplay'
import useCallout from '../../hooks/useCallout'
import { ICallout } from '../../models/Callout'

interface IGetHelpStepProps {
  step: number
  header?: string
  contentMain?: string
  contentGuidance?: string
  choices: IChoiceOption[]
  value?: number
  loading: boolean
  forward: (value: ISelectedChoice) => void
  backward: (fromStep: number) => void
}

const GetHelpDecisionStep = (props: IGetHelpStepProps) => {
  const mountedRef = useRef(false)
  const [selectedChoice, setSelectedChoice] = useState<ISelectedChoice>(null)
  const [choices, setChoices] = useState<IChoiceOption[]>()
  const [value, setValue] = useState(null)
  const [active, setActive] = useState(false)
  const loader = useContext(FormDependencyLoaderContext)
  const [data, setData] = useState<ICallout[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [getCallout] = useCallout('GetHelp', 'Before')

  useEffect(() => {
    mountedRef.current = true
    if (mountedRef.current) {
      props.choices?.map((item) => {
        item.ref = createRef() //This way we get a ref to dynamic content
      })

      setSelectedChoice(null)
      setChoices(props.choices)
      setValue(props.value || 0)

      if (props.value > 0) {
        const result = props.choices?.find((k) => k.id === props.value)
        if (result) {
          setSelectedChoice((prevState) => ({
            ...prevState,
            step: props.step,
            id: props.value,
            isLinkedContent: result.isLinkedContent,
          }))
        }
      }
    }
    window.scrollTo(0, 0)
    return () => {
      mountedRef.current = false
    }
  }, [props.choices])

  const radioHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const choiceId = Number(event.target.value)
    setSelectedChoice((prevState) => ({
      ...prevState,
      step: props.step,
      id: choiceId,
      isLinkedContent: props.choices.find((d) => d.id === choiceId)
        ?.isLinkedContent,
    }))
    setValue(choiceId)
  }

  const showChoiceContent = (option: IChoiceOption) => {
    option.ref.current.clear()
  }

  const renderChoices = (option: IChoiceOption, index: number) => {
    return (
      <div className="choice" key={index}>
        <GetHelpOption
          value={value}
          option={option}
          radioHandler={radioHandler}
        />
        <Conditional if={option.choiceContent?.length > 0}>
          <button
            className="help-button"
            onClick={() => showChoiceContent(option)}
          >
            <FontAwesomeIcon
              tabIndex={0}
              icon={faInfoCircle}
              title={option.choiceContent}
              size="2x"
            />
          </button>
        </Conditional>
        <Conditional if={option.choiceContent?.length > 0}>
          <GetHelpOptionContent
            key={index}
            ref={option.ref}
            content={option.choiceContent}
          />
        </Conditional>
      </div>
    )
  }

  //The replacement in this method is a temporary solution. Once DB is updated, it should be reverted.
  const createReplaceMarkup = (content: string) => {
    if (content === null || content === undefined) {
      return createMarkup(content)
    }

    content = content.replaceAll(
      '/images/UserUploadedImages/151/open-new-tab-icon.png',
      '/public/open-new-tab-icon.png'
    )
    return createMarkup(content)
  }

  const backward = () => {
    props.backward(props.step)
  }

  const forward = (selectedChoice: ISelectedChoice) => {
    const choice: IChoiceOption = props.choices?.find(
      (k) => k.id === selectedChoice.id
    )
    if (choice === null || choice === undefined) {
      return
    }

    if (isEmpty(choice.url) || !isURL(choice.url)) {
      props.forward(selectedChoice)
      return
    }

    window.open(choice.url, '_blank')
  }

  useEffect(() => {
    mountedRef.current = true

    if (mountedRef.current) {
      getCallout()
        .then((response) => {
          if (mountedRef.current) {
            setData(response.callouts)
          }
        })
        .catch((error) => {
          console.log(error)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
    return () => {
      mountedRef.current = false
    }
  }, [])

  return (
    <Conditional if={!loader.isLoading}>
      <h1>Get help</h1>

      <Conditional if={!isLoading}>
        {data &&
          data.map((c, i) => (
            <CalloutDisplay key={i} content={c.content} name="GetHelp" />
          ))}
      </Conditional>
      <h2 className="h2span">{props.header}</h2>
      <hr />
      <Conditional if={props.contentMain?.length > 0}>
        <div dangerouslySetInnerHTML={createReplaceMarkup(props.contentMain)} />
      </Conditional>
      <Conditional if={props.contentGuidance?.length > 0}>
        <button className="help-button" onClick={() => setActive(!active)}>
          <FontAwesomeIcon tabIndex={0} icon={faInfoCircle} size="2x" />
        </button>
        <Conditional if={active}>
          <div className="highlight blue">
            <p
              dangerouslySetInnerHTML={createReplaceMarkup(
                props.contentGuidance
              )}
            ></p>
          </div>
        </Conditional>
      </Conditional>
      <Conditional if={choices}>
        <div className="choice-list">
          {choices?.map((option, index) => {
            return renderChoices(option, index)
          })}
        </div>
        <div className="text-center">
          <Conditional if={props.step > 0}>
            <Button
              onClick={backward}
              className="is-secondary"
              type="button"
              disabled={props.loading}
            >
              Back
            </Button>

            <Conditional if={selectedChoice !== null}>
              <Button
                onClick={() => forward(selectedChoice)}
                className="is-primary"
                type="button"
                disabled={selectedChoice === null || props.loading}
              >
                Next
              </Button>
            </Conditional>
          </Conditional>
          <Conditional if={props.step <= 0}>
            <Button
              onClick={() => forward(selectedChoice)}
              className="is-primary"
              type="button"
              disabled={selectedChoice === null || props.loading}
            >
              Continue
            </Button>
          </Conditional>
        </div>
      </Conditional>
    </Conditional>
  )
}

export default GetHelpDecisionStep
